<script setup>
import { computed, nextTick, ref, watch } from "vue";
import { useMediaQuery } from "@vueuse/core";
import debounce from "lodash/debounce";
import { useTriviaStore } from "@stores/trivia";
import { useGlobeStore } from "@stores/globe";
import { useGlobalStore } from "@stores/global";
import { useWorldStore } from "@stores/world";
import IconClock from "@components/icons/Clock.vue";
import IconArrowRight from "@components/icons/ArrowRight.vue";
import IconX from "@components/icons/X.vue";
import TextInput from "@components/TextInput.vue";
import FeedbackChip from "@components/trivia/FeedbackChip.vue";

const triviaStore = useTriviaStore();
const globeStore = useGlobeStore();
const globalStore = useGlobalStore();
const worldStore = useWorldStore();

const isMobile = useMediaQuery("(max-width: 1024px)");
const activeTab = ref("guess");

function closeMobile() {
    globalStore.openModal("leaveTriviaConfirm", { payload: "explore" });
}

const input = ref("");
const selectedCountry = ref(null);
const highlightedIndex = ref(0);
const searchListEl = ref(null);

watch(highlightedIndex, async () => {
    await nextTick();
    const el = searchListEl.value?.children[highlightedIndex.value];
    el?.scrollIntoView({ block: "nearest" });
});
const focused = ref(false);
let blurTimer = null;

const query = computed(() => input.value.trim().toLowerCase());

const searchResults = computed(() => {
    if (!query.value) return [];
    return worldStore.searchableCountries.filter((c) =>
        c.name.toLowerCase().includes(query.value),
    );
});

const showResults = computed(
    () => focused.value && searchResults.value.length > 0,
);

const roundNumber = computed(() => triviaStore.currentRound + 1);

const canSubmit = computed(
    () =>
        !!selectedCountry.value &&
        !triviaStore.loading &&
        !triviaStore.hasCorrectGuess,
);

watch(
    () => globeStore.selectedCountry,
    (name) => {
        if (!name) return;
        input.value = name;
        const match = worldStore.searchableCountries.find(
            (c) => c.name === name,
        );
        if (match) selectedCountry.value = match;
    },
);

const checkExactMatch = debounce((val) => {
    const q = val.trim().toLowerCase();
    if (!q) return;
    const exact = worldStore.searchableCountries.find(
        (c) => c.name.toLowerCase() === q,
    );
    if (exact) selectedCountry.value = exact;
}, 200);

watch(input, (val) => {
    highlightedIndex.value = 0;
    if (selectedCountry.value && val !== selectedCountry.value.name) {
        selectedCountry.value = null;
    }
    if (val.trim().length > 0 && document.activeElement?.tagName === "INPUT") {
        focused.value = true;
    }
    checkExactMatch(val);
});

function selectCountry(country) {
    input.value = country.name;
    selectedCountry.value = country;
    focused.value = false;
}

async function submit() {
    if (!canSubmit.value) return;
    await triviaStore.submitGuess(selectedCountry.value);
    input.value = "";
    selectedCountry.value = null;
    globeStore.clearSelection();
}

function onEnter() {
    if (showResults.value && searchResults.value[highlightedIndex.value]) {
        selectCountry(searchResults.value[highlightedIndex.value]);
        submit();
        return;
    }
    submit();
}

function moveHighlight(delta) {
    if (!showResults.value) return;
    const len = searchResults.value.length;
    highlightedIndex.value = (highlightedIndex.value + delta + len) % len;
}

function onFocus() {
    clearTimeout(blurTimer);
    focused.value = true;
}

function onBlur() {
    blurTimer = setTimeout(() => {
        focused.value = false;
    }, 150);
}
</script>

<template>
    <Transition name="fade" appear>
        <div
            v-if="
                triviaStore.gameState === 'playing' &&
                triviaStore.photoPosition === 'docked' &&
                !triviaStore.hasCorrectGuess
            "
            class="trivia-input-bar bg-marine-800 relative flex flex-col px-10 py-2 max-lg:gap-0 max-lg:px-0 max-lg:pt-2 max-lg:pb-0 lg:justify-center lg:gap-2"
        >
            <!-- Mobile header -->
            <div
                class="hidden items-center justify-between px-4 pt-4 max-lg:flex"
            >
                <div class="flex flex-col gap-3">
                    <p class="body-sm leading-[0.9] font-bold uppercase">
                        Mini Game
                    </p>
                    <p
                        class="h4 leading-[0.9] font-extrabold text-yellow-400 uppercase"
                    >
                        Trivia
                    </p>
                </div>
                <button
                    class="border-marine-600 shadow-marine-600 flex size-8 shrink-0 items-center justify-center rounded-[4px] border-2 bg-yellow-400 shadow-[0_4px_0]"
                    @click="closeMobile"
                >
                    <IconX class="text-marine-600 size-3" />
                </button>
            </div>

            <!-- Mobile tabs -->
            <div class="border-marine-400 hidden border-b px-4 max-lg:flex">
                <button
                    v-for="tab in [
                        { id: 'guess', label: 'Guess' },
                        { id: 'round', label: 'Round Guesses' },
                    ]"
                    :key="tab.id"
                    :class="[
                        'body-sm flex h-10 flex-1 items-center justify-center border-b-2 px-6 py-4 font-medium uppercase transition-colors',
                        activeTab === tab.id
                            ? 'border-yellow-400 text-yellow-400'
                            : 'faded-text border-transparent',
                    ]"
                    @click="activeTab = tab.id"
                >
                    {{ tab.label }}
                </button>
            </div>

            <!-- Guess tab content -->
            <div
                v-show="!isMobile || activeTab === 'guess'"
                class="flex flex-col max-lg:gap-2 max-lg:p-4 lg:gap-2"
            >
                <!-- Desktop 4-col stats -->
                <div class="grid h-14 w-full grid-cols-4 gap-0 max-lg:hidden">
                    <div
                        class="bg-marine-800 border-marine-500 flex items-center justify-between border p-4 uppercase"
                    >
                        <span
                            class="body-md faded-text leading-[0.9] font-bold tracking-[-0.02em]"
                        >
                            Score
                        </span>
                        <span
                            class="body-lg text-temperature-correct leading-[0.8] font-extrabold"
                        >
                            {{ triviaStore.totalPoints }}
                        </span>
                    </div>
                    <div
                        class="bg-marine-800 border-marine-500 flex items-center justify-between border border-l-0 p-4 uppercase"
                    >
                        <span
                            class="body-md faded-text leading-[0.9] font-bold tracking-[-0.02em]"
                        >
                            Round
                        </span>
                        <span
                            class="body-lg flex items-center gap-1 leading-[0.8]"
                        >
                            <span class="font-extrabold">{{
                                roundNumber
                            }}</span>
                            <span class="text-marine-200 font-medium">/</span>
                            <span class="text-marine-200 font-extrabold">
                                {{ triviaStore.totalRounds }}
                            </span>
                        </span>
                    </div>
                    <div
                        class="bg-marine-800 border-marine-500 flex items-center justify-between border border-l-0 p-4 uppercase"
                    >
                        <span
                            class="body-md faded-text leading-[0.9] font-bold tracking-[-0.02em]"
                        >
                            Guesses
                        </span>
                        <span class="body-lg leading-[0.8] font-extrabold">
                            {{ triviaStore.roundGuesses.length }}
                        </span>
                    </div>
                    <div
                        class="text-marine-800 flex items-center justify-between bg-yellow-400 p-4"
                    >
                        <IconClock class="size-6" />
                        <span
                            class="flex items-end leading-[0.8] font-extrabold uppercase"
                        >
                            <span class="body-lg flex">
                                <span
                                    v-for="(
                                        ch, i
                                    ) in triviaStore.formattedTimer"
                                    :key="`t-${i}`"
                                    class="inline-block w-[0.6em] text-center lg:w-[0.575em]"
                                >
                                    {{ ch }}
                                </span>
                            </span>
                            <span class="body-sm flex opacity-70">
                                <span
                                    class="inline-block w-[0.6em] text-center lg:w-[0.575em]"
                                    >:</span
                                >
                                <span
                                    v-for="(
                                        ch, i
                                    ) in triviaStore.formattedTimerMs"
                                    :key="`ms-${i}`"
                                    class="inline-block w-[0.55em] text-center"
                                    >{{ ch }}</span
                                >
                            </span>
                        </span>
                    </div>
                </div>

                <!-- Input row -->
                <div
                    class="relative mb-2 w-full max-lg:grid max-lg:h-12 max-lg:grid-cols-3 max-lg:gap-2 lg:mb-0 lg:flex lg:h-14 lg:items-center"
                >
                    <div
                        class="search-list bg-marine-800 border-marine-500 absolute right-0 left-0 z-10 flex flex-col border max-lg:top-full max-lg:border-t-0 max-lg:pt-2 lg:bottom-full lg:border-b-0 lg:pb-2"
                        v-if="showResults"
                    >
                        <div
                            class="border-marine-500 flex h-6 items-center border-b px-6 py-2"
                        >
                            <p
                                class="body-xs faded-text leading-[0.9] font-bold tracking-[-0.02em] uppercase"
                            >
                                {{ searchResults.length }} results for &ldquo;{{
                                    input.trim()
                                }}&rdquo;
                            </p>
                        </div>
                        <ul
                            ref="searchListEl"
                            class="search-list__scroll bg-marine-800 overflow-y-auto max-lg:max-h-[calc(50svh-14rem)] lg:max-h-[156px]"
                        >
                            <li
                                v-for="(country, idx) in searchResults"
                                :key="country.id"
                            >
                                <button
                                    type="button"
                                    :class="[
                                        'body-sm flex h-7 w-full items-center px-6 py-4 text-left leading-[0.8] font-medium uppercase transition-colors',
                                        idx === highlightedIndex
                                            ? 'bg-marine-700'
                                            : 'hover:bg-marine-700',
                                    ]"
                                    @mousedown.prevent="selectCountry(country)"
                                    @mouseenter="highlightedIndex = idx"
                                >
                                    {{ country.name }}
                                </button>
                            </li>
                        </ul>
                    </div>

                    <span
                        class="absolute top-0 left-0 size-1.5 bg-yellow-400 max-lg:hidden lg:block"
                    ></span>
                    <div
                        class="flex h-full items-center max-lg:col-span-2 lg:flex-1"
                        @focusin="onFocus"
                        @focusout="onBlur"
                    >
                        <TextInput
                            v-model="input"
                            :placeholder="
                                globeStore.hoveredCountry ??
                                'Enter a country name'
                            "
                            input-class="w-full border-marine-500 border h-full px-6 max-lg:px-4 max-lg:text-sm"
                            class="flex-1"
                            autofocus
                            @keydown.enter.prevent="onEnter"
                            @keydown.down.prevent="moveHighlight(1)"
                            @keydown.up.prevent="moveHighlight(-1)"
                        />
                        <div class="group/tooltip relative max-lg:hidden">
                            <button
                                type="button"
                                :disabled="!canSubmit"
                                class="group border-marine-500 -ml-px flex size-14 shrink-0 cursor-pointer items-center justify-center border transition-colors duration-300 enabled:bg-yellow-400 disabled:opacity-40"
                                aria-label="Submit guess"
                                @click="submit"
                            >
                                <svg
                                    v-if="triviaStore.loading"
                                    class="size-4 animate-spin text-yellow-400"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                >
                                    <circle
                                        cx="12"
                                        cy="12"
                                        r="10"
                                        stroke="currentColor"
                                        stroke-width="3"
                                        stroke-opacity="0.25"
                                    />
                                    <path
                                        d="M22 12a10 10 0 0 0-10-10"
                                        stroke="currentColor"
                                        stroke-width="3"
                                        stroke-linecap="round"
                                    />
                                </svg>
                                <IconArrowRight
                                    v-else
                                    class="group-enabled:text-marine-900 size-4 text-yellow-400 transition-transform duration-200 group-enabled:group-hover:translate-x-1"
                                />
                            </button>
                            <div class="pointer-events-none absolute bottom-full left-1/2 mb-2 -translate-x-1/2 whitespace-nowrap rounded bg-marine-900 px-2 py-1 text-xs text-white opacity-0 transition-opacity duration-200 group-hover/tooltip:opacity-100">
                                Submit Guess
                                <div class="absolute left-1/2 top-full -translate-x-1/2 border-4 border-transparent border-t-marine-900"></div>
                            </div>
                        </div>
                    </div>

                    <!-- Mobile timer (inline with input) -->
                    <div
                        class="bg-marine-600 flex h-full items-center justify-center gap-2 px-2 lg:hidden"
                    >
                        <IconClock class="size-5 text-yellow-400" />
                        <span
                            class="flex items-end leading-[0.8] font-extrabold text-yellow-400 uppercase"
                        >
                            <span class="body-lg flex">
                                <span
                                    v-for="(
                                        ch, i
                                    ) in triviaStore.formattedTimer"
                                    :key="`mt-${i}`"
                                    class="inline-block w-[0.6em] text-center lg:w-[0.575em]"
                                >
                                    {{ ch }}
                                </span>
                            </span>
                            <span class="body-sm flex opacity-70">
                                <span
                                    class="inline-block w-[0.6em] text-center lg:w-[0.575em]"
                                    >:</span
                                >
                                <span
                                    v-for="(
                                        ch, i
                                    ) in triviaStore.formattedTimerMs"
                                    :key="`mms-${i}`"
                                    class="inline-block w-[0.55em] text-center"
                                    >{{ ch }}</span
                                >
                            </span>
                        </span>
                    </div>
                </div>

                <!-- Mobile 2-col stats -->
                <div class="grid h-12 w-full grid-cols-2 gap-2 lg:hidden">
                    <div
                        class="bg-marine-800 border-marine-500 flex items-center justify-between border p-4 uppercase"
                    >
                        <span
                            class="body-md faded-text leading-[0.9] font-bold tracking-[-0.02em]"
                        >
                            Score
                        </span>
                        <span
                            class="body-lg text-temperature-correct leading-[0.8] font-extrabold"
                        >
                            {{ triviaStore.totalPoints }}
                        </span>
                    </div>
                    <div
                        class="bg-marine-800 border-marine-500 flex items-center justify-between border p-4 uppercase"
                    >
                        <span
                            class="body-md faded-text leading-[0.9] font-bold tracking-[-0.02em]"
                        >
                            Guesses
                        </span>
                        <span class="body-lg leading-[0.8] font-extrabold">
                            {{ triviaStore.roundGuesses.length }}
                        </span>
                    </div>
                </div>

                <!-- Rating bar -->
                <div class="flex w-full max-lg:pt-3 lg:pt-3">
                    <div class="flex w-full items-center justify-center">
                        <div class="rating-skew w-full">
                            <div class="flex h-1.5 w-full items-center gap-1">
                                <div
                                    v-for="i in triviaStore.totalRounds"
                                    :key="i"
                                    :class="[
                                        'h-full flex-1',
                                        i <= roundNumber
                                            ? 'bg-yellow-400'
                                            : 'bg-marine-500',
                                    ]"
                                ></div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Round guesses tab (mobile only) -->
            <div
                v-if="isMobile && activeTab === 'round'"
                class="hidden flex-col gap-1 p-4 max-lg:flex"
            >
                <p
                    v-if="triviaStore.roundGuesses.length === 0"
                    class="body-sm faded-text"
                >
                    Your guesses will appear here.
                </p>
                <ul v-else class="flex flex-col gap-1">
                    <li
                        v-for="(guess, i) in triviaStore.roundGuesses"
                        :key="i"
                        class="border-marine-500 grid h-10 grid-cols-3 items-center border border-l-2 px-4"
                        :style="{ borderLeftColor: guess.temperatureHex }"
                    >
                        <span class="body-sm leading-none font-bold uppercase">
                            {{ guess.countryName }}
                        </span>
                        <span
                            class="body-sm faded-text text-right leading-none font-medium uppercase"
                        >
                            {{ guess.distance }}
                        </span>
                        <FeedbackChip
                            :name="guess.temperatureName"
                            :hex="guess.temperatureHex"
                        />
                    </li>
                </ul>
            </div>

            <!-- Mobile confirm and submit button -->
            <button
                type="button"
                :disabled="!canSubmit"
                class="body-sm text-marine-700 mt-auto flex h-12 w-full items-center justify-center bg-yellow-400 font-extrabold uppercase disabled:opacity-40 max-lg:flex lg:mt-0 lg:hidden"
                @click="submit"
            >
                <svg
                    v-if="triviaStore.loading"
                    class="size-4 animate-spin"
                    viewBox="0 0 24 24"
                    fill="none"
                >
                    <circle
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        stroke-width="3"
                        stroke-opacity="0.25"
                    />
                    <path
                        d="M22 12a10 10 0 0 0-10-10"
                        stroke="currentColor"
                        stroke-width="3"
                        stroke-linecap="round"
                    />
                </svg>
                <span v-else>Confirm and submit</span>
            </button>
        </div>
    </Transition>
</template>

<style scoped>
.trivia-input-bar {
    grid-column: 6 / -6;
    grid-row: -4 / -1;
    z-index: 3;
}

@media (max-width: 1024px) {
    .trivia-input-bar {
        grid-column: 1 / -1;
        grid-row: -2 / -1;
        align-self: end;
        height: 50svh;
        max-height: 50svh;
        overflow-y: auto;
    }
}

.rating-skew {
    transform: skewX(-48deg) scaleY(0.67);
}

.search-list__scroll::-webkit-scrollbar {
    width: 5px;
}
.search-list__scroll::-webkit-scrollbar-track {
    background: var(--color-marine-500);
}
.search-list__scroll::-webkit-scrollbar-thumb {
    background: var(--color-yellow-400);
}
</style>
